import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { 
  Calendar, 
  Archive, 
  CheckCircle2, 
  Clock, 
  ChevronDown,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  Settings2
} from 'lucide-react';
import { format } from 'date-fns';
import { ExportButton } from '../utils/exportUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IndentSummaryPage = ({ user }) => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [poSeqOpen, setPoSeqOpen] = useState(false);
  const [poSeqInfo, setPoSeqInfo] = useState(null);
  const [newSeq, setNewSeq] = useState('');

  const currentMonth = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    fetchSummaries();
  }, [fromDate, toDate]);

  const fetchSummaries = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      const res = await axios.get(`${API}/indents/summaries?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummaries(res.data);
    } catch {
      toast.error('Failed to fetch indent summaries');
    } finally {
      setLoading(false);
    }
  };

  const fetchPoSeq = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/po-sequence`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPoSeqInfo(res.data);
      setNewSeq(res.data.current_seq.toString());
      setPoSeqOpen(true);
    } catch {
      toast.error('Failed to fetch PO sequence');
    }
  };

  const handleUpdateSeq = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/po-sequence?new_seq=${parseInt(newSeq)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setPoSeqOpen(false);
      fetchPoSeq();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update sequence');
    }
  };

  const handleCloseMonth = async () => {
    setClosing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/indents/close-month`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setCloseConfirmOpen(false);
      fetchSummaries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close month');
    } finally {
      setClosing(false);
    }
  };

  const isCurrentMonthClosed = summaries.some(s => s.month === currentMonth);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Indent Summary</h1>
          <p className="text-slate-600 mt-1">Monthly purchase indent closure and history</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.is_super_admin && (
            <Button variant="outline" onClick={fetchPoSeq} data-testid="po-seq-btn">
              <Settings2 className="h-4 w-4 mr-2" />
              PO Sequence
            </Button>
          )}
          {user?.is_admin && (
            <Button
              className={isCurrentMonthClosed ? 'bg-slate-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}
              onClick={() => !isCurrentMonthClosed && setCloseConfirmOpen(true)}
              disabled={isCurrentMonthClosed}
              data-testid="close-month-btn"
            >
              <Archive className="h-4 w-4 mr-2" />
              {isCurrentMonthClosed ? `${currentMonth} Closed` : `Close ${currentMonth}`}
            </Button>
          )}
          <Button variant="outline" onClick={fetchSummaries}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="flex gap-4 items-end bg-white p-4 rounded-lg border">
        <div>
          <Label className="text-xs">From Month</Label>
          <Input type="month" value={fromDate} onChange={e => setFromDate(e.target.value)} className="mt-1 w-40" />
        </div>
        <div>
          <Label className="text-xs">To Month</Label>
          <Input type="month" value={toDate} onChange={e => setToDate(e.target.value)} className="mt-1 w-40" />
        </div>
        <Button variant="outline" onClick={() => { setFromDate(''); setToDate(''); }}>Clear</Button>
      </div>

      {/* Summaries */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900" />
        </div>
      ) : summaries.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border">
          <Archive className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No closed months yet. Close a month to generate a summary.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {summaries.map(s => (
            <div key={s.id} className="bg-white rounded-lg border shadow-sm overflow-hidden">
              {/* Summary Header */}
              <div
                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50"
                onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
              >
                <div className="flex items-center gap-4">
                  {expandedId === s.id ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-slate-900">{s.month}</span>
                      <Badge className="bg-slate-800 text-white text-xs">Closed</Badge>
                      <span onClick={e => e.stopPropagation()}>
                        <ExportButton
                          rows={[...(s.issued_items || []), ...(s.pending_items || [])]}
                          columns={[
                            { header: 'REQ #', key: 'pr_number' },
                            { header: 'Part', key: 'part_name' },
                            { header: 'Asset', key: 'door_no' },
                            { header: 'Qty Requested', key: 'quantity_requested' },
                            { header: 'Qty Received', key: 'quantity_received' },
                            { header: 'Status', key: 'status' },
                            { header: 'Source', key: 'source' },
                          ]}
                          filename={`indent_summary_${s.month}`}
                          title={`Monthly Indent Summary — ${s.month}`}
                          subtitle={`Closed by ${s.closed_by} | ${s.total_issued} issued, ${s.total_pending} pending`}
                        />
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Closed by {s.closed_by} on {format(new Date(s.closed_at), 'dd MMM yyyy, HH:mm')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-6 text-center">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{s.total_raised}</p>
                    <p className="text-xs text-slate-500">Total Raised</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{s.total_issued}</p>
                    <p className="text-xs text-slate-500">Issued</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-500">{s.total_pending}</p>
                    <p className="text-xs text-slate-500">Not Issued</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-700">₹{(s.total_value_ordered || 0).toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Value Issued</p>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === s.id && (
                <div className="border-t">
                  <div className="grid grid-cols-2 divide-x">
                    {/* Issued Items */}
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <h3 className="font-semibold text-sm text-green-700">Issued Items ({s.issued_items?.length || 0})</h3>
                      </div>
                      {(s.issued_items || []).length === 0 ? (
                        <p className="text-sm text-slate-400">None</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">REQ #</TableHead>
                              <TableHead className="text-xs">Part</TableHead>
                              <TableHead className="text-xs">Asset</TableHead>
                              <TableHead className="text-xs text-center">Qty</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {s.issued_items.map((item, i) => (
                              <TableRow key={i}>
                                <TableCell className="font-mono text-xs">{item.pr_number}</TableCell>
                                <TableCell className="text-xs">{item.part_name}</TableCell>
                                <TableCell className="text-xs">{item.door_no || '—'}</TableCell>
                                <TableCell className="text-xs text-center">{item.quantity_received}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>

                    {/* Pending/Not Issued Items */}
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="h-4 w-4 text-orange-500" />
                        <h3 className="font-semibold text-sm text-orange-600">Not Issued ({s.pending_items?.length || 0})</h3>
                      </div>
                      {(s.pending_items || []).length === 0 ? (
                        <p className="text-sm text-slate-400">All items issued!</p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-xs">REQ #</TableHead>
                              <TableHead className="text-xs">Part</TableHead>
                              <TableHead className="text-xs">Asset</TableHead>
                              <TableHead className="text-xs">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {s.pending_items.map((item, i) => (
                              <TableRow key={i}>
                                <TableCell className="font-mono text-xs">{item.pr_number}</TableCell>
                                <TableCell className="text-xs">{item.part_name}</TableCell>
                                <TableCell className="text-xs">{item.door_no || '—'}</TableCell>
                                <TableCell>
                                  <Badge className="bg-red-100 text-red-700 text-xs">{item.status}</Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Close Month Confirm Dialog */}
      <Dialog open={closeConfirmOpen} onOpenChange={setCloseConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Close Month — {currentMonth}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              This will <strong>close all indents for {currentMonth}</strong>:
            </p>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>All pending/approved PRs will be <strong className="text-red-600">cancelled</strong></li>
              <li>A summary of issued vs not-issued will be <strong>saved permanently</strong></li>
              <li>Fresh indents start from the 1st of next month</li>
              <li>This action <strong>cannot be undone</strong></li>
            </ul>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCloseConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleCloseMonth} className="bg-red-600 hover:bg-red-700" disabled={closing}>
              {closing ? 'Closing...' : `Confirm Close ${currentMonth}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PO Sequence Editor — Super Admin Only */}
      <Dialog open={poSeqOpen} onOpenChange={setPoSeqOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit PO Number Sequence</DialogTitle>
          </DialogHeader>
          {poSeqInfo && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 text-sm">
                <p>Financial Year: <strong>{poSeqInfo.financial_year}</strong></p>
                <p className="mt-1">Current counter: <strong>{poSeqInfo.current_seq}</strong></p>
                <p className="mt-1">Next PO will be: <strong className="text-blue-700 font-mono">{poSeqInfo.next_po}</strong></p>
              </div>
              <div>
                <Label>Set Counter To</Label>
                <Input
                  type="number"
                  min="0"
                  value={newSeq}
                  onChange={e => setNewSeq(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Next PO will be: LSN/HO/SFT/{poSeqInfo.financial_year}/{String(parseInt(newSeq || 0) + 1).padStart(3, '0')}
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPoSeqOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateSeq} className="bg-slate-900 hover:bg-slate-800" data-testid="save-seq-btn">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IndentSummaryPage;
